// API Test Dashboard
// Provides a comprehensive interface for API testing

import React, { useState, useEffect } from 'react';
import { apiTestAutomation, APITestRunResult } from '../services/apiTestAutomation';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Play, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Activity,
  Database,
  Zap,
  BarChart3,
  Download,
  Trash2,
  AlertCircle
} from 'lucide-react';

const APITestDashboard: React.FC = () => {
  const [testResults, setTestResults] = useState<APITestRunResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<any>(null);

  const runTests = async () => {
    setIsRunning(true);
    setError(null);
    try {
      const results = await apiTestAutomation.runAPITests();
      setTestResults(results);
      loadStatistics();
    } catch (err) {
      console.error('Error running API tests:', err);
      setError('Failed to run API tests. Check console for details.');
    } finally {
      setIsRunning(false);
    }
  };

  const loadStatistics = () => {
    const stats = apiTestAutomation.getTestStatistics();
    setStatistics(stats);
  };

  const loadLatestResults = () => {
    const latest = apiTestAutomation.getLatestTestResults();
    setTestResults(latest);
  };

  const clearResults = () => {
    apiTestAutomation.clearTestRuns();
    setTestResults(null);
    loadStatistics();
  };

  const exportResults = () => {
    const data = testResults ? apiTestAutomation.exportTestResults(testResults.sessionId) : apiTestAutomation.exportTestResults();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-test-results-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadLatestResults();
    loadStatistics();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'skip':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'fail':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'skip':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
            <Database className="w-8 h-8 mr-3" />
            API Test Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive testing of backend services, Firebase operations, and data flow
          </p>
        </div>

        {/* Control Panel */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Test Control Panel</CardTitle>
            <CardDescription>Run API tests and manage test results</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-4">
            <Button onClick={runTests} disabled={isRunning} className="flex-1">
              {isRunning ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Running Tests...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run API Tests
                </>
              )}
            </Button>
            <Button onClick={loadLatestResults} disabled={isRunning} variant="outline" className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Results
            </Button>
            <Button onClick={exportResults} disabled={!testResults} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button onClick={clearResults} disabled={!testResults} variant="outline">
              <Trash2 className="mr-2 h-4 w-4" />
              Clear
            </Button>
          </CardContent>
          {error && (
            <CardFooter>
              <Badge variant="destructive" className="flex items-center">
                <AlertCircle className="mr-2 h-4 w-4" />
                {error}
              </Badge>
            </CardFooter>
          )}
        </Card>

        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Activity className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Runs</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics.totalRuns}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <BarChart3 className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Success Rate</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics.averageSuccessRate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Zap className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tests</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics.totalTests}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-orange-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Duration</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics.averageDuration}ms</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Test Results */}
        {testResults && (
          <div className="space-y-6">
            {/* Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Test Summary</CardTitle>
                <CardDescription>
                  Session: {testResults.sessionId} | {testResults.timestamp.toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-center">
                  <div className="p-4 border rounded-md dark:border-gray-700">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{testResults.summary.successRate}%</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Success Rate</p>
                  </div>
                  <div className="p-4 border rounded-md dark:border-gray-700">
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{testResults.summary.totalTests}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Tests</p>
                  </div>
                  <div className="p-4 border rounded-md dark:border-gray-700">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{testResults.summary.passedTests}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Passed</p>
                  </div>
                  <div className="p-4 border rounded-md dark:border-gray-700">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{testResults.summary.failedTests}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Failed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Test Suites */}
            {testResults.suites.map((suite, suiteIndex) => (
              <Card key={suiteIndex}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{suite.name}</span>
                    <div className="flex items-center space-x-2">
                      <Badge variant={suite.passedTests === suite.totalTests ? 'success' : 'destructive'}>
                        {suite.passedTests}/{suite.totalTests} passed
                      </Badge>
                      <span className="text-sm text-gray-500">{suite.duration}ms</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64 w-full">
                    <div className="space-y-2">
                      {suite.tests.map((test, testIndex) => (
                        <div key={testIndex} className="flex items-center space-x-3 p-3 border rounded-md dark:border-gray-700">
                          {getStatusIcon(test.status)}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100">{test.testName}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{test.message}</p>
                            {test.error && (
                              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{test.error}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge className={getStatusColor(test.status)}>
                              {test.status.toUpperCase()}
                            </Badge>
                            <span className="text-sm text-gray-500">{test.duration}ms</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results State */}
        {!testResults && (
          <Card>
            <CardContent className="p-12 text-center">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Test Results</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Run API tests to see comprehensive backend service testing results
              </p>
              <Button onClick={runTests} disabled={isRunning}>
                <Play className="mr-2 h-4 w-4" />
                Run Your First Test
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default APITestDashboard;
